import { cn } from '@/lib/utils';
import { LazyVideo } from './lazy-video';

const optionClass = (selected: boolean) =>
  cn(
    'rounded-lg border border-border text-left text-sm transition-colors hover:border-primary/50',
    selected && 'border-primary bg-primary/5 ring-1 ring-primary',
  );

/** Radio row of picture options (stage photo or a muted style loop, swatch while it loads) with the label on top. */
export function SwatchPicker({
  options,
  value,
  onChange,
}: {
  options: readonly { id: string; label: string; swatch: string; image?: string; video?: string }[];
  value: string;
  onChange: (id: string) => void;
}) {
  return (
    <div className="grid grid-cols-4 gap-2 sm:gap-3" role="radiogroup">
      {options.map((option) => {
        const selected = option.id === value;
        return (
          <button
            key={option.id}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(option.id)}
            className={cn(
              'group relative aspect-[3/4] overflow-hidden rounded-xl text-left ring-1 ring-border transition-all',
              selected ? 'ring-2 ring-primary ring-offset-2 ring-offset-card' : 'opacity-80 hover:opacity-100',
            )}
            style={{ background: option.swatch }}
          >
            {option.video ? (
              <LazyVideo src={option.video} poster={option.image} label={option.label} className="absolute inset-0 size-full" />
            ) : option.image ? (
              <img src={option.image} alt="" loading="lazy" className="absolute inset-0 size-full object-cover" />
            ) : null}
            <span className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/0" aria-hidden="true" />
            <span className="absolute inset-x-2 bottom-2 text-[11px] font-semibold leading-tight text-white sm:text-xs">
              {option.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Stacked radio list with a right-aligned meta value (quality tier + credits). */
export function MetaOptionList({
  options,
  value,
  onChange,
}: {
  options: readonly { id: string; label: string; meta: string }[];
  value: string;
  onChange: (id: string) => void;
}) {
  return (
    <div className="space-y-2" role="radiogroup">
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          role="radio"
          aria-checked={option.id === value}
          onClick={() => onChange(option.id)}
          className={cn('flex w-full items-center justify-between gap-3 px-3 py-2.5', optionClass(option.id === value))}
        >
          <span className="font-medium">{option.label}</span>
          <span className="shrink-0 text-xs font-semibold text-muted-foreground">{option.meta}</span>
        </button>
      ))}
    </div>
  );
}
